import { useState, useMemo } from 'react';
import type { AttendanceSegment, AttendanceStatus } from '@aegislab/shared';
import type { CheckInStatus } from '../../../types';
import { formatDurationKo, formatTimeShort, getWorkPhase } from '../../../lib';
import { PowerIcon, PauseIcon, PlayIcon, SignOutIcon } from '../../atoms/Icon';
import { Button } from '../../atoms/Button';
import { ConfirmDialog } from '../../atoms/ConfirmDialog';
import * as s from './CheckInCenter.css';

export interface CheckInCenterTodayData {
  status: AttendanceStatus;
  clockInAt: string;
  clockOutAt: string | null;
  totalWorkDurationMs: number;
  segments: AttendanceSegment[];
  totalBreakDurationMs: number;
  /** 근태 비고. '자동 마감' 포함 시 시스템 강제 마감으로 간주(정정 안내). */
  note?: string | null;
}

export interface CheckInCenterProps {
  checkInStatus: CheckInStatus;
  elapsedSeconds: number;
  onCheckIn: () => void;
  onPause: () => void;
  onResume: () => void;
  onCheckOut: () => void;
  isLoading?: boolean;
  todayData?: CheckInCenterTodayData;
  targetWorkDurationMs?: number;
}

export function CheckInCenter({
  checkInStatus,
  elapsedSeconds,
  onCheckIn,
  onPause,
  onResume,
  onCheckOut,
  isLoading = false,
  todayData,
  targetWorkDurationMs,
}: CheckInCenterProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isClockedOut =
    checkInStatus === 'idle' && todayData?.status === 'CLOCKED_OUT' && !!todayData?.clockInAt;

  const phase = useMemo(
    () =>
      getWorkPhase(elapsedSeconds * 1000, targetWorkDurationMs ?? 0, checkInStatus === 'paused'),
    [elapsedSeconds, targetWorkDurationMs, checkInStatus]
  );

  const glowOverlay = (
    <>
      <span className={s.glowEffect} aria-hidden />
      <span className={s.glowBleedEffect} aria-hidden />
    </>
  );

  // ── idle (출근 전) ──
  if (checkInStatus === 'idle' && !isClockedOut) {
    return (
      <div className={s.container}>
        {glowOverlay}
        <button
          type="button"
          // 온보딩 첫날 안내 1단계가 이 버튼을 하이라이트 링으로 지목한다 (값 = step.id)
          data-onboarding-target="clock-in"
          className={`${s.actionButton} ${s.clockInButton} ${s.clockInPulse}`}
          onClick={onCheckIn}
          disabled={isLoading}
        >
          <span className={s.iconWrapper}>
            <PowerIcon size={24} />
          </span>
          출근하기
        </button>
      </div>
    );
  }

  // ── 퇴근 완료 / 자동 마감 ──
  if (isClockedOut && todayData) {
    // 시스템이 강제 마감한 기록은 실제 퇴근이 아니므로 일반 퇴근과 구분 표시하고
    // 재출근을 1차 CTA로 강조하지 않는다(오인 재클릭 루프 억제).
    const autoClosed = !!todayData.note && todayData.note.includes('자동 마감');
    return (
      <div className={s.container}>
        {glowOverlay}
        <div className={s.statusRow}>
          <span className={`${s.statusDot} ${autoClosed ? s.statusDotAuto : s.statusDotDone}`} />
          <span className={autoClosed ? s.autoClosedLabel : s.statusLabel}>
            {autoClosed ? '자동 마감' : '퇴근'}
          </span>
          <span className={s.clockInTime}>{formatTimeShort(todayData.clockInAt)}</span>
          <span className={s.arrow}>→</span>
          <span className={s.clockOutTime}>
            {todayData.clockOutAt ? formatTimeShort(todayData.clockOutAt) : ''}
          </span>
          <span className={s.elapsed}>{formatDurationKo(todayData.totalWorkDurationMs)}</span>
        </div>
        {autoClosed && (
          <p className={s.autoClosedHint}>
            시스템이 자동 마감한 기록이에요 · 실제 퇴근 시각이 다르면 정정해 주세요
          </p>
        )}
        <div className={s.actions}>
          <button
            type="button"
            className={`${s.actionButton} ${autoClosed ? s.reclockMutedButton : s.clockInButton}`}
            onClick={onCheckIn}
            disabled={isLoading}
          >
            <span className={s.iconWrapper}>
              <PowerIcon size={18} />
            </span>
            재출근
          </button>
        </div>
      </div>
    );
  }

  // ── working / paused ──
  const isWorking = checkInStatus === 'working';
  const isPaused = checkInStatus === 'paused';

  const phaseStyle = {
    '--phase-glow-opacity': String(phase.glowOpacity),
    '--phase-breathe-duration': phase.breatheDuration,
  } as React.CSSProperties;

  return (
    <div className={s.container} style={phaseStyle}>
      {glowOverlay}
      <div className={s.statusRow}>
        <span className={`${s.statusDot} ${isWorking ? s.statusDotWorking : s.statusDotPaused}`} />
        <span className={s.statusLabel}>{isWorking ? '근무 중' : '휴식 중'}</span>
        {todayData && (
          <span className={s.clockInTime}>출근 {formatTimeShort(todayData.clockInAt)}</span>
        )}
        <span className={s.elapsed}>{phase.display}</span>
      </div>

      <div className={s.actions}>
        {isWorking && (
          <Button
            variant="soft"
            tone="warning"
            size="md"
            className={s.actionGrow}
            leftIcon={
              <span className={s.iconWrapper}>
                <PauseIcon size={18} />
              </span>
            }
            onClick={onPause}
            disabled={isLoading}
          >
            휴식
          </Button>
        )}
        {isPaused && (
          <button
            type="button"
            className={`${s.actionButton} ${s.resumeButton}`}
            onClick={onResume}
            disabled={isLoading}
          >
            <span className={s.iconWrapper}>
              <PlayIcon size={18} />
            </span>
            재개
          </button>
        )}
        <Button
          variant="soft"
          tone="danger"
          size="md"
          className={s.actionGrow}
          leftIcon={
            <span className={s.iconWrapper}>
              <SignOutIcon size={18} />
            </span>
          }
          onClick={() => setConfirmOpen(true)}
          disabled={isLoading}
        >
          퇴근
        </Button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          onCheckOut();
        }}
        title="퇴근 확인"
        message="정말 퇴근하시겠습니까?"
        confirmLabel="퇴근"
        cancelLabel="취소"
        variant="danger"
      />
    </div>
  );
}
